import { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { usePortfolio } from '../../context/PortfolioContext';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const COLORS = {
  positive: '#00e5a0',
  negative: '#ff3d5a',
};

function PositionPnLChart() {
  const { holdings } = usePortfolio();

  // Track mobile breakpoint so we can tighten bar spacing only on phones.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 640px)').matches : false,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const chartData = useMemo(() => {
    if (!holdings?.length) return null;

    // Filter positions with quantity > 0, map to chart data
    const positions = [...holdings]
      .filter(h => Number(h.quantity || 0) > 0)
      .map(h => ({
        ticker: h.ticker?.toUpperCase() || 'UNKNOWN',
        pnl: Number(h.unrealized_gbp || 0) + Number(h.realized_gbp || h.realGbp || 0),
        quantity: Number(h.quantity || 0),
      }));

    // Sort by absolute P&L, take top 10
    const sorted = positions
      .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
      .slice(0, 10);

    if (sorted.length === 0) return null;

    return {
      labels: sorted.map(h => h.ticker),
      datasets: [{
        label: 'P&L (GBP)',
        data: sorted.map(h => h.pnl),
        backgroundColor: sorted.map(h => h.pnl >= 0 ? COLORS.positive : COLORS.negative),
        borderRadius: 4,
        borderSkipped: false,
        // Tighter bars on mobile: thicker bars + bars almost touch their category slot.
        maxBarThickness: isMobile ? 32 : 24,
        minBarLength: 8,
        barPercentage: isMobile ? 0.98 : 0.8,
        categoryPercentage: isMobile ? 0.98 : 0.9,
      }],
    };
  }, [holdings, isMobile]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(12, 28, 48, 0.96)',
        borderColor: 'rgba(0, 212, 255, 0.35)',
        borderWidth: 1,
        titleColor: '#dff0ff',
        bodyColor: '#dff0ff',
        padding: 10,
        callbacks: {
          label: (context) => {
            const value = context.parsed.y;
            return new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }).format(value);
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 10 },
          maxRotation: 45,
          minRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
        barPercentage: 0.8,
        categoryPercentage: 0.9,
      },
      y: {
        grid: {
          color: 'rgba(29, 56, 85, 0.25)',
          drawBorder: false,
        },
        ticks: {
          color: '#7a99b8',
          font: { family: 'Space Mono', size: 10 },
          callback: (value) => {
            return new Intl.NumberFormat('en-GB', {
              style: 'currency',
              currency: 'GBP',
              notation: 'compact',
              maximumFractionDigits: 1,
            }).format(value);
          },
        },
      },
    },
  };

  if (!chartData) {
    return (
      <div className="chart-container">
        <div className="chart-header">
          <span className="chart-title">P&L by Position</span>
        </div>
        <div style={{ height: '250px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span className="text-muted">No position data</span>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <span className="chart-title">P&L by Position</span>
        <span className="data-table-badge">Performance</span>
      </div>
      <div className="chart-wrapper" style={{ height: '250px' }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
}

export default PositionPnLChart;
